import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";

import ConfirmMessageModal from "components/ConfirmMessageModal";
import {
  AssignmentWorkflowStep,
  getAssignmentNoticeToClientPreview,
  getAssignmentProgress,
  sendAssignmentNoticeToClient,
  type AssignmentEmailPreview,
  type AssignmentNoticePdfItem,
} from "../../api/assignment";

export const ASSIGNMENT_NOTICE_TO_CLIENT_OF_ASSIGNMENT_FORM_ID =
  "assignment-notice-to-client-of-assignment-form";

type AssignmentNoticeToClientOfAssignmentViewProps = {
  leadId?: string | null;
  onSuccess?: (leadId: string) => void;
  onSentStatusChange?: (sent: boolean) => void;
};

export default function AssignmentNoticeToClientOfAssignmentView({
  leadId,
  onSuccess,
  onSentStatusChange,
}: AssignmentNoticeToClientOfAssignmentViewProps) {
  const [phoneNumber, setPhoneNumber] = useState("");
  const [emailAddress, setEmailAddress] = useState("");
  const [items, setItems] = useState<AssignmentNoticePdfItem[]>([]);
  const [emailPreview, setEmailPreview] = useState<AssignmentEmailPreview | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [hasSentWhatsapp, setHasSentWhatsapp] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmMode, setConfirmMode] = useState<"send" | "resend">("send");

  useEffect(() => {
    onSentStatusChange?.(hasSentWhatsapp);
  }, [hasSentWhatsapp, onSentStatusChange]);

  // Workflow progress – is this step already completed (sent)?
  useEffect(() => {
    if (!leadId) {
      setHasSentWhatsapp(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const progress = await getAssignmentProgress(leadId);
        const step = progress.steps.find(
          (s) => s.slug === AssignmentWorkflowStep.AS_S3_02_NoticeToClient
        );
        if (!cancelled && step) {
          setHasSentWhatsapp(step.isCompleted);
        }
      } catch {
        if (!cancelled) setHasSentWhatsapp(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [leadId]);

  // Load preview from backend so the displayed text always matches what will be sent.
  useEffect(() => {
    if (!leadId) return;
    let cancelled = false;
    setPreviewLoading(true);
    setPreviewError(null);
    getAssignmentNoticeToClientPreview(leadId)
      .then((res) => {
        if (cancelled) return;
        setItems(res.items ?? []);
        setPhoneNumber(res.recipientPhone ?? "");
        setEmailAddress(res.recipientEmail ?? "");
        setEmailPreview(res.emailPreview ?? null);
      })
      .catch((err) => {
        if (cancelled) return;
        const msg =
          err instanceof Error
            ? err.message
            : "Failed to load message preview";
        setPreviewError(msg);
        setItems([]);
        setEmailPreview(null);
      })
      .finally(() => {
        if (!cancelled) setPreviewLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [leadId]);

  const confirmPreviewSlot = useMemo(() => {
    if (!items.length) return null;
    return (
      <div
        className="flex flex-col gap-2 p-4"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        {emailPreview && (
          <p
            className="font-sans text-[12px] leading-5 font-medium text-[#222] m-0"
            style={{ fontFeatureSettings: "'ss01' on, 'cv01' on" }}
          >
            Email subject: {emailPreview.subject}
          </p>
        )}
        <div className="flex flex-col gap-1 mt-1">
          {items.map((item, idx) => (
            <p
              key={`${item.applicationId}-${item.kind}-${idx}`}
              className="font-sans text-[12px] leading-5 text-[#222] m-0"
              style={{ fontFeatureSettings: "'ss01' on, 'cv01' on" }}
            >
              ·{" "}
              {item.kind === "assignment_intimation"
                ? "Assignment Intimation"
                : "Status of Application"}{" "}
              – TM No. {item.tmNo}
            </p>
          ))}
        </div>
        <p
          className="font-sans text-[11px] leading-4 text-[#6D6D6D] m-0 mt-1"
          style={{ fontFeatureSettings: "'ss01' on, 'cv01' on" }}
        >
          WhatsApp: {items.length} individual message{items.length > 1 ? "s" : ""} with download link · Email: 1 combined email
        </p>
      </div>
    );
  }, [items, emailPreview]);

  const canSend = !previewError && items.length > 0;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId || sending) return;
    if (hasSentWhatsapp) {
      onSuccess?.(leadId);
      return;
    }
    if (!canSend) {
      toast.error(
        previewError ??
          "Upload Assignment Intimation / Status of Application PDFs for at least one application first."
      );
      return;
    }
    setConfirmMode("send");
    setShowConfirmModal(true);
  };

  const handleConfirmProceed = () => {
    if (!leadId) return;
    setShowConfirmModal(false);
    setSending(true);
    (async () => {
      try {
        await sendAssignmentNoticeToClient(leadId);
        setHasSentWhatsapp(true);
        toast.success(
          confirmMode === "resend"
            ? "Notice resent to client successfully."
            : "Notice sent to client successfully."
        );
        onSuccess?.(leadId);
      } catch (err) {
        toast.error(err instanceof Error ? err.message : "Failed to send");
      } finally {
        setSending(false);
      }
    })();
  };

  const handleResend = () => {
    if (!leadId || sending) return;
    if (!canSend) {
      toast.error(
        previewError ??
          "Upload Assignment Intimation / Status of Application PDFs for at least one application first."
      );
      return;
    }
    setConfirmMode("resend");
    setShowConfirmModal(true);
  };

  if (!leadId) {
    return (
      <div
        className="flex flex-col gap-[22px] w-full max-w-[1085px] justify-center py-12"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        <p className="text-sm text-[#6D6D6D]">
          Please complete the previous steps (select or create a lead) to access
          this step.
        </p>
      </div>
    );
  }

  return (
    <form
      id={ASSIGNMENT_NOTICE_TO_CLIENT_OF_ASSIGNMENT_FORM_ID}
      onSubmit={handleSubmit}
      className="flex flex-col gap-[22px] w-full max-w-[1085px]"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      <div className="flex flex-col gap-5">
        <p className="text-[16px] leading-5 text-[#222]">Preview</p>
        <div className="rounded-[6px] border border-[#EEEEEE] bg-white p-[14px] min-h-[80px] flex flex-col gap-2">
          {previewLoading && items.length === 0 ? (
            <p
              className="font-sans text-[12px] leading-5 text-[#6D6D6D] m-0"
              style={{ fontFeatureSettings: "'ss01' on, 'cv01' on" }}
            >
              Loading preview...
            </p>
          ) : items.length > 0 ? (
            <>
              {emailPreview && (
                <p
                  className="font-sans text-[12px] leading-5 font-medium text-[#222] m-0"
                  style={{ fontFeatureSettings: "'ss01' on, 'cv01' on" }}
                >
                  Email subject: {emailPreview.subject}
                </p>
              )}
              <div className="flex flex-col gap-1 mt-1">
                {items.map((item, idx) => (
                  <p
                    key={`${item.applicationId}-${item.kind}-${idx}`}
                    className="font-sans text-[12px] leading-5 text-[#222] m-0"
                    style={{ fontFeatureSettings: "'ss01' on, 'cv01' on" }}
                  >
                    ·{" "}
                    {item.kind === "assignment_intimation"
                      ? "Assignment Intimation"
                      : "Status of Application"}{" "}
                    – TM No. {item.tmNo}
                  </p>
                ))}
              </div>
              <p
                className="font-sans text-[11px] leading-4 text-[#6D6D6D] m-0 mt-1"
                style={{ fontFeatureSettings: "'ss01' on, 'cv01' on" }}
              >
                WhatsApp: {items.length} individual message{items.length > 1 ? "s" : ""} with download link · Email: 1 combined email
              </p>
            </>
          ) : (
            <p
              className="font-sans text-[12px] leading-5 text-[#6D6D6D] m-0"
              style={{ fontFeatureSettings: "'ss01' on, 'cv01' on" }}
            >
              {previewError ??
                "Upload Assignment Intimation / Status of Application PDFs in the previous step to generate the preview."}
            </p>
          )}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <input
          type="tel"
          inputMode="numeric"
          value={phoneNumber}
          readOnly
          placeholder="e.g. +919876543210"
          className="w-full min-w-0 sm:w-[200px] h-9 px-[10px] py-2 text-[12px] text-[#222] border border-[#EEEEEE] rounded-[6px] bg-[#F7F7F7] placeholder:text-[#6D6D6D]"
        />
        <input
          type="email"
          value={emailAddress}
          readOnly
          placeholder="Client email"
          className="w-full min-w-0 sm:w-[240px] h-9 px-[10px] py-2 text-[12px] text-[#222] border border-[#EEEEEE] rounded-[6px] bg-[#F7F7F7] placeholder:text-[#6D6D6D]"
        />
        {hasSentWhatsapp && (
          <button
            type="button"
            disabled={sending || !canSend}
            onClick={handleResend}
            className="inline-flex items-center justify-center h-9 px-4 text-[12px] font-medium leading-5 text-[#2E3037] bg-white border border-[#D5D3E0] rounded-[10px] whitespace-nowrap hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {sending ? "Sending…" : "Resend Message"}
          </button>
        )}
      </div>

      <ConfirmMessageModal
        open={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        previewSlot={confirmPreviewSlot}
        onConfirm={handleConfirmProceed}
        disableConfirm={!canSend}
      />
    </form>
  );
}
